/**
 * Hook useContactDealTags
 * Busca todas as tags de negócio (CRM) associadas a um contato
 * Agrega tags de todos os deals do contato (sem duplicação)
 *
 * Versão: 2.0.0
 * RELEASE: 2026-04-19
 * - Migrado para RPC `get_contact_deal_tags` (SECURITY DEFINER)
 * - Remove acesso direto às tabelas crm_deals e crm_deal_tag_assignments
 * - Agregação realizada no servidor (1 chamada em vez de 2)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('ContactDealTags');

// Interface de tag agregada (sem duplicação)
export interface ContactDealTag {
  id: string;
  name: string;
  color: string;
  /** Quantos deals têm essa tag */
  deals_count: number;
}

// Interface de deal com suas tags
export interface ContactDealWithTags {
  id: string;
  title: string;
  status: string;
  tags: Array<{ id: string; name: string; color: string }>;
}

// Retorno do hook
export interface ContactDealTagsData {
  /** Tags agregadas (únicas) de todos os deals */
  tags: ContactDealTag[];
  /** Lista de deals do contato com suas tags */
  deals: ContactDealWithTags[];
}

// Opções do hook
interface UseContactDealTagsOptions {
  /** Habilitar/desabilitar a query (lazy loading) */
  enabled?: boolean;
}

/**
 * Hook para buscar tags de negócio de um contato
 * Agrega tags de todos os deals do contato (sem duplicação) — via RPC server-side
 */
export function useContactDealTags(
  contactId: string | null | undefined,
  options?: UseContactDealTagsOptions
) {
  const { currentTenant } = useAuth();

  // Query só executa se CRM está habilitado e temos tenant/contato
  const isEnabled = (options?.enabled ?? true)
    && !!contactId
    && !!currentTenant?.id
    && !!currentTenant?.crm_enabled;

  return useQuery({
    queryKey: ['contact-deal-tags', contactId, currentTenant?.id],
    queryFn: async (): Promise<ContactDealTagsData> => {
      if (!contactId || !currentTenant?.id) {
        return { tags: [], deals: [] };
      }

      logger.debug('Buscando tags de negócio do contato (RPC):', contactId);

      // Chamada única à RPC server-side: agregação já vem pronta
      const { data, error } = await supabase.rpc('get_contact_deal_tags', {
        p_contact_id: contactId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar tags de negócio via RPC:', error.message);
        throw error;
      }

      // Payload da RPC: { tags: [...], deals: [...] }
      const payload = (data ?? { tags: [], deals: [] }) as unknown as ContactDealTagsData;
      const tags = Array.isArray(payload.tags) ? payload.tags : [];
      const deals = Array.isArray(payload.deals) ? payload.deals : [];

      logger.debug(`Tags recebidas: ${tags.length} únicas / ${deals.length} deals`);

      return { tags, deals };
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache por 30 segundos
  });
}
